// apps/web/src/features/crm/leads/activity/components/LeadTaskEditorDrawer.tsx
//
// Create/reschedule form for a manager's own MANUAL lead self-task
// (#1804), rendered inside an `EditorDrawer` so the unsaved-changes guard
// is auto-wired. `dirty={formState.isDirty}` is the data-loss boundary, so
// `useForm` lives in the SAME component that mounts `EditorDrawer` —
// otherwise the live dirty flag could not reach the drawer's close guard.
//
// `dueAt` on the wire is a full ISO 8601 string (or null). The native
// `<input type="date">` speaks `YYYY-MM-DD`, so we slice on display and
// re-anchor to UTC midnight on submit; an empty field clears the anchor
// (NULL), which removes the task from My Day.
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CreateLeadTaskInputSchema,
  RescheduleLeadTaskInputSchema,
  LEAD_TASK_TITLE_MAX,
  type CreateLeadTaskInput,
  type RescheduleLeadTaskInput,
  type LeadTask,
} from '@magimp/shared';
import { useCreateLeadTask, useRescheduleLeadTask } from '@magimp/shared-hooks';
import { Button, EditorDrawer, FormRow, Input, useEditorGuard } from '../../../../../ui/index.js';
import { useToastStore } from '../../../../../stores/toast.js';
import { errorMessage } from '../../../../../api/errors.js';

export interface LeadTaskEditorDrawerProps {
  leadId: string;
  /** Present → edit/reschedule mode; absent → create mode. */
  task?: LeadTask;
  onClose: () => void;
}

/** ISO 8601 timestamp → `YYYY-MM-DD` for the native date input. */
function isoToDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/** `YYYY-MM-DD` (or empty) → UTC-midnight ISO timestamp, or null when cleared. */
function dateInputToIso(value: string): string | null {
  if (value === '') return null;
  return new Date(`${value}T00:00:00Z`).toISOString();
}

export function LeadTaskEditorDrawer({ leadId, task, onClose }: LeadTaskEditorDrawerProps) {
  return task ? (
    <RescheduleDrawer task={task} onClose={onClose} />
  ) : (
    <CreateDrawer leadId={leadId} onClose={onClose} />
  );
}

function CreateDrawer({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const { t } = useTranslation(['leads', 'common']);
  const show = useToastStore((s) => s.show);
  const create = useCreateLeadTask();
  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<CreateLeadTaskInput>({
    resolver: zodResolver(CreateLeadTaskInputSchema),
    defaultValues: { leadId, title: '', dueAt: null },
  });

  const onSubmit = (values: CreateLeadTaskInput) => {
    create.mutate(values, {
      onSuccess: () => {
        show({ tone: 'success', text: t('leads:tasks.toasts.created') });
        onClose();
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <EditorDrawer dirty={isDirty} busy={create.isPending} onConfirmClose={onClose} width={420}>
      <TaskFormBody
        title={t('leads:tasks.form.createTitle')}
        isDirty={isDirty}
        busy={create.isPending}
        onSubmit={handleSubmit(onSubmit)}
        onClose={onClose}
      >
        <FormRow label={t('leads:tasks.form.titleLabel')} error={errors.title?.message}>
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                placeholder={t('leads:tasks.form.titlePlaceholder')}
                maxLength={LEAD_TASK_TITLE_MAX}
                invalid={!!errors.title}
              />
            )}
          />
        </FormRow>
        <FormRow label={t('leads:tasks.form.dueLabel')} error={errors.dueAt?.message}>
          <Controller
            control={control}
            name="dueAt"
            render={({ field }) => (
              <Input
                type="date"
                value={isoToDateInput(field.value ?? null)}
                onChange={(v) => field.onChange(dateInputToIso(v))}
                invalid={!!errors.dueAt}
              />
            )}
          />
        </FormRow>
      </TaskFormBody>
    </EditorDrawer>
  );
}

function RescheduleDrawer({ task, onClose }: { task: LeadTask; onClose: () => void }) {
  const { t } = useTranslation(['leads', 'common']);
  const show = useToastStore((s) => s.show);
  const reschedule = useRescheduleLeadTask(task.id);
  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<RescheduleLeadTaskInput>({
    resolver: zodResolver(RescheduleLeadTaskInputSchema),
    defaultValues: { title: task.title, dueAt: task.dueAt },
  });

  const onSubmit = (values: RescheduleLeadTaskInput) => {
    reschedule.mutate(values, {
      onSuccess: () => {
        show({ tone: 'success', text: t('leads:tasks.toasts.rescheduled') });
        onClose();
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <EditorDrawer dirty={isDirty} busy={reschedule.isPending} onConfirmClose={onClose} width={420}>
      <TaskFormBody
        title={t('leads:tasks.form.editTitle')}
        isDirty={isDirty}
        busy={reschedule.isPending}
        onSubmit={handleSubmit(onSubmit)}
        onClose={onClose}
      >
        <FormRow label={t('leads:tasks.form.titleLabel')} error={errors.title?.message}>
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <Input
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder={t('leads:tasks.form.titlePlaceholder')}
                maxLength={LEAD_TASK_TITLE_MAX}
                invalid={!!errors.title}
              />
            )}
          />
        </FormRow>
        <FormRow label={t('leads:tasks.form.dueLabel')} error={errors.dueAt?.message}>
          <Controller
            control={control}
            name="dueAt"
            render={({ field }) => (
              <Input
                type="date"
                value={isoToDateInput(field.value ?? null)}
                onChange={(v) => field.onChange(dateInputToIso(v))}
                invalid={!!errors.dueAt}
              />
            )}
          />
        </FormRow>
      </TaskFormBody>
    </EditorDrawer>
  );
}

// `dirty` is surfaced on the form element (`data-dirty`) for the guard test
// and drives the EditorDrawer's close arbitration via its parent's prop.
function TaskFormBody({
  title,
  isDirty,
  busy,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  isDirty: boolean;
  busy: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation(['common']);
  // WHY: Cancel routes through confirmDestructive so a dirty form prompts before discarding.
  const { confirmDestructive } = useEditorGuard();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      noValidate
      data-testid="lead-task-editor"
      data-dirty={isDirty ? 'true' : 'false'}
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{title}</h2>
      {children}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button kind="ghost" onClick={() => confirmDestructive(onClose)} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" kind="primary" disabled={busy} data-testid="lead-task-editor-save">
          {t('common:actions.save')}
        </Button>
      </div>
    </form>
  );
}
